import { useEffect, useState } from 'react';
import { Copy, KeyRound, LogIn, LogOut, Mail, Users } from 'lucide-react';
import { formatFriendCode, type AuthProviderId } from '@gmp/shared-types/browser';
import { Avatar, Sheet, Stage } from '@gmp/shared-ui';
import { navigate } from '../app/router';
import { sound } from '../app/prefs';
import { useAccountsConfig } from '../app/serverConfig';
import { usePartyState } from '../App';
import { party } from '../net/client';
import { beginSignIn, deleteAccount, emailStart, emailVerify, redeem, signOut, signOutEverywhere, unlinkLogin } from '../net/account';
import { ProfileFields } from './Profile';
import { PROVIDER_BUTTON, ProviderIcon } from '../app/ProviderIcon';

/**
 * Optional accounts: sign in with whichever methods this server has configured (never a button for one
 * that isn't), or carry on as a guest exactly as before.
 */
export function SignInSheet({ open, onClose, title = 'Sign in (optional)' }: { open: boolean; onClose: () => void; title?: string }) {
  const accounts = useAccountsConfig();
  const { signedIn } = usePartyState();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'pick' | 'code'>('pick');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  useEffect(() => {
    if (!open) {
      setStep('pick');
      setCode('');
      setProblem(null);
      setDevCode(null);
    }
  }, [open]);
  const providers = accounts?.providers ?? [];
  const oauth = providers.filter((p): p is { id: Exclude<AuthProviderId, 'email'>; name: string } => p.id !== 'email');
  const hasEmail = providers.some((p) => p.id === 'email');

  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true);
    setProblem(null);
    const err = await fn();
    setBusy(false);
    if (err) setProblem(err);
    return err;
  };

  const sendCode = () => run(async () => {
    const r = await emailStart(email);
    if (!r.ok) return r.error;
    setDevCode(r.value.devCode ?? null);
    setStep('code');
    return null;
  });

  const verify = () => run(async () => {
    const err = await emailVerify(email, code);
    if (!err) {
      sound.play('join');
      party.notify(signedIn ? 'Login added' : 'Signed in', 'good');
      onClose();
    }
    return err;
  });

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <div className="grid gap-4" data-testid="signin-sheet">
        <p className="text-mute text-sm">
          {signedIn
            ? 'Add another way to sign in to the same account.'
            : 'Sign in to keep your parties on every device, get a friend code and invite friends who are online. Your current parties come with you.'}
        </p>
        {providers.length === 0 && <p className="text-mute">Sign-in isn’t available on this server.</p>}
        {step === 'pick' && oauth.map((p) => (
          <button key={p.id} className={PROVIDER_BUTTON[p.id]?.className ?? 'btn btn-secondary'} style={PROVIDER_BUTTON[p.id]?.style} disabled={busy} onClick={() => void run(() => beginSignIn(p.id))} data-testid={`signin-${p.id}`}>
            <ProviderIcon id={p.id} size={22} /> Continue with {p.name}
          </button>
        ))}
        {hasEmail && step === 'pick' && (
          <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); void sendCode(); }}>
            <label className="grid gap-1">
              <span className="text-sm font-bold text-mute">Email (we send you a 6-digit code)</span>
              <input className="field" type="email" inputMode="email" autoComplete="email" required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="signin-email" />
            </label>
            <button className="btn" disabled={busy || !email.includes('@')} data-testid="signin-email-send"><ProviderIcon id="email" size={20} /> Email me a code</button>
          </form>
        )}
        {step === 'code' && (
          <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); void verify(); }}>
            <p className="text-sm">We sent a code to <strong className="selectable">{email}</strong>. It works for 10 minutes.</p>
            {devCode && <p className="chip text-xs" data-testid="signin-dev-code">Test mode (no email sent): {devCode}</p>}
            <label className="grid gap-1">
              <span className="text-sm font-bold text-mute">6-digit code</span>
              <input className="field font-display text-2xl tracking-[0.3em] tabular-nums" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} data-testid="signin-code" />
            </label>
            <button className="btn" disabled={busy || code.length !== 6} data-testid="signin-code-submit"><KeyRound size={18} aria-hidden /> Sign in</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStep('pick')}>Use a different email</button>
          </form>
        )}
        {problem && <p className="chip bg-bad/20 border-bad text-sm py-2 px-3" role="alert">{problem}</p>}
        {!signedIn && <button className="btn btn-ghost" onClick={onClose} data-testid="signin-guest">Continue as guest</button>}
        <p className="text-xs text-mute">Guests can keep playing exactly as now. Friends only ever see your account name, character and whether you are online.</p>
      </div>
    </Sheet>
  );
}

/** A small entry on Home: "Sign in" (only when the server offers it) or "Friends" once signed in. */
/**
 * Home screen, top corner: "Sign in" (opens the sign-in sheet) or, once signed in, your name and character
 * (opens Friends, with a count of new requests and invites). Hidden when this server has no login method.
 */
export function AccountEntry() {
  const accounts = useAccountsConfig();
  const { signedIn, social } = usePartyState();
  const [open, setOpen] = useState(false);
  if (!accounts && !signedIn) return null;
  const waiting = (social?.incoming.length ?? 0) + (social?.invites.length ?? 0);
  const me = social?.me;
  return (
    <>
      {signedIn ? (
        <button className="btn btn-sm btn-secondary max-w-[60vw]" onClick={() => { sound.play('tap'); navigate({ name: 'friends' }); }} data-testid="home-friends" aria-label={`Friends${waiting ? `, ${waiting} new` : ''}`}>
          {me ? <Avatar id={me.avatar} size={24} /> : <Users size={18} aria-hidden />}
          <span className="truncate">{me?.displayName ?? 'Friends'}</span>
          {waiting > 0 && <span className="rounded-full bg-[var(--accent)] text-ink text-xs font-black px-1.5 min-w-5 text-center">{waiting}</span>}
        </button>
      ) : (
        <button className="btn btn-sm btn-gold" onClick={() => { sound.play('tap'); setOpen(true); }} data-testid="home-signin">
          <LogIn size={18} aria-hidden /> Sign in
        </button>
      )}
      <SignInSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

const PROVIDER_LABEL: Record<AuthProviderId, string> = { email: 'Email', google: 'Google', facebook: 'Facebook', discord: 'Discord', gmg: 'Grudge Match Games' };

/** Settings → Account: linked logins, friend code, display name, sign out. */
export function AccountPanel() {
  const accounts = useAccountsConfig();
  const { signedIn, social } = usePartyState();
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [name, setName] = useState('');
  const me = social?.me;
  useEffect(() => {
    if (me) setName(me.displayName);
  }, [me?.displayName]);
  if (!accounts && !signedIn) return null;
  if (!signedIn) {
    return (
      <section className="card p-5 grid gap-3" aria-label="Account">
        <h2 className="font-display font-black text-xl">Account (optional)</h2>
        <p className="text-mute text-sm">You are playing as a guest. Sign in to use the same identity on other devices and to add friends.</p>
        <button className="btn justify-self-start" onClick={() => setOpen(true)} data-testid="settings-signin"><LogIn size={18} aria-hidden /> Sign in</button>
        <SignInSheet open={open} onClose={() => setOpen(false)} />
      </section>
    );
  }
  const labels = (accounts?.providers ?? []).reduce<Record<string, string>>((m, p) => ({ ...m, [p.id]: p.name }), {});
  const linked = new Set(me?.logins.map((l) => l.provider));
  const canAdd = (accounts?.providers ?? []).some((p) => !linked.has(p.id));
  return (
    <section className="card p-5 grid gap-4" aria-label="Account" data-testid="account-panel">
      <h2 className="font-display font-black text-xl">Your account</h2>
      {!me ? <p className="text-mute">Loading…</p> : (
        <>
          <div className="flex items-center gap-3">
            <Avatar id={me.avatar} size={56} />
            <div className="min-w-0">
              <p className="text-xs text-mute">Your friend code</p>
              <p className="selectable font-display font-black text-2xl tracking-wider tabular-nums" data-testid="my-friend-code">{formatFriendCode(me.friendCode)}</p>
            </div>
            <button className="btn btn-sm btn-secondary ml-auto" aria-label="Copy friend code" onClick={() => navigator.clipboard?.writeText(formatFriendCode(me.friendCode)).then(() => party.notify('Friend code copied', 'good'))}><Copy size={16} /></button>
          </div>
          <details className="grid gap-2">
            <summary className="cursor-pointer font-bold text-sm">Name and character friends see</summary>
            <div className="grid gap-3 mt-3">
              <ProfileFields nickname={name} avatar={me.avatar} onNickname={setName} onAvatar={(a) => void party.social({ op: 'update', avatar: a })} />
              <button className="btn btn-sm btn-secondary justify-self-start" disabled={!name.trim() || name.trim() === me.displayName} onClick={() => void party.social({ op: 'update', displayName: name.trim() }).then((r) => party.notify(r.ok ? 'Saved' : r.error ?? 'Not saved', r.ok ? 'good' : 'bad'))}>Save name</button>
            </div>
          </details>
          <div className="grid gap-2">
            <p className="text-sm font-bold text-mute">Signed in with</p>
            <ul className="grid gap-2">
              {me.logins.map((l) => (
                <li key={l.provider} className="flex items-center gap-2 text-sm" data-testid={`login-${l.provider}`}>
                  <span className="chip"><ProviderIcon id={l.provider} size={14} /> {labels[l.provider] ?? PROVIDER_LABEL[l.provider]}</span>
                  <span className="text-mute truncate selectable">{l.email ?? ''}</span>
                  {me.logins.length > 1 && <button className="btn btn-sm btn-ghost ml-auto" onClick={() => void unlinkLogin(l.provider).then(setProblem)}>Remove</button>}
                </li>
              ))}
            </ul>
            {canAdd && <button className="btn btn-sm btn-secondary justify-self-start" onClick={() => setOpen(true)}>Add another login</button>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-sm" onClick={() => navigate({ name: 'friends' })}><Users size={16} aria-hidden /> Friends</button>
            <button className="btn btn-sm btn-secondary" onClick={() => void signOut().then(() => party.notify('Signed out. You are a guest on this device.'))} data-testid="signout"><LogOut size={16} aria-hidden /> Sign out</button>
            <button className="btn btn-sm btn-ghost" onClick={() => { if (confirm('Sign out on every device, this one included?')) void signOutEverywhere().then(setProblem); }}>Sign out everywhere</button>
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-mute">Delete my account</summary>
            <div className="grid gap-2 mt-2">
              <p className="text-mute">Deletes your account, logins, friends, requests, blocks and recent players for good. This device keeps playing as a guest, and your open parties stay open.</p>
              <button className="btn btn-sm btn-secondary text-bad justify-self-start" onClick={() => { if (confirm('Delete your account for good?')) void deleteAccount().then((e) => (e ? setProblem(e) : party.notify('Account deleted'))); }} data-testid="delete-account">Delete my account</button>
            </div>
          </details>
          {problem && <p className="chip bg-bad/20 border-bad text-sm py-2 px-3" role="alert">{problem}</p>}
        </>
      )}
      <SignInSheet open={open} onClose={() => setOpen(false)} title="Add a login" />
    </section>
  );
}

const DONE_ERRORS: Record<string, string> = {
  cancelled: 'Sign-in was cancelled.',
  expired: 'That sign-in took too long or was already used. Try again.',
  taken: 'That login already belongs to another account.',
  provider: 'The sign-in service didn’t confirm who you are. Try again.',
  failed: 'Sign-in failed. Try again.',
};

/** /auth/done: where a provider sign-in lands (one-time handoff code in the URL fragment). */
export function AuthDone() {
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    const q = new URLSearchParams(window.location.hash.slice(1));
    history.replaceState(null, '', '/auth/done'); // the handoff never stays in history
    const err = q.get('error');
    const handoff = q.get('h');
    const back = q.get('r');
    const to = back && back.startsWith('/') && !back.startsWith('//') ? back : '/';
    if (err || !handoff) return setProblem(DONE_ERRORS[err ?? ''] ?? DONE_ERRORS.failed!);
    void redeem(handoff).then((e) => {
      if (e) return setProblem(e);
      // Reload so every part of the app starts over with the signed-in identity.
      window.location.replace(to === '/auth/done' ? '/' : to);
    });
  }, []);
  return (
    <Stage>
      <main className="flex-1 grid place-items-center safe-pad safe-top safe-bottom p-6">
        <div className="card p-6 max-w-md flex flex-col gap-4 text-center" role="status">
          {problem ? (
            <>
              <h1 className="font-display font-black text-2xl">Not signed in</h1>
              <p className="text-mute">{problem}</p>
              <button className="btn" onClick={() => navigate({ name: 'home' }, true)}>Home</button>
            </>
          ) : (
            <h1 className="font-display font-black text-2xl">Signing you in…</h1>
          )}
        </div>
      </main>
    </Stage>
  );
}
